<script lang="ts">
  import { type HomepageClippingHeroProps, OverlayType } from '../types.js';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';

  export let text: HomepageClippingHeroProps['text'];
  export let buttonLinks: HomepageClippingHeroProps['buttonLinks'];
  export let videoLink: HomepageClippingHeroProps['videoLink'];
  export let type: OverlayType;
</script>

<div class="absolute {type} overlay" aria-hidden={type === OverlayType.Reveal}>
  <div class="sticky-text">
    <div class="hero-text">
      <div class:invisible={type === OverlayType.Reveal}>
        <DynamicChildrenComponent
          children={[text[0]]}
          passThroughProps={{
            customCssClass: 'HomepageClippingHero__content',
          }}
        />
      </div>

      {#if text.length > 1}
        <div class="reveal-text">
          <DynamicChildrenComponent children={[text[1]]} />
        </div>
      {/if}
      <div class="button-line">
        <DynamicChildrenComponent
          children={buttonLinks}
          passThroughProps={{
            customCssClass: 'HomepageClippingHero__link',
          }}
        />
      </div>
    </div>
    {#if videoLink.length > 0 && type === OverlayType.Before}
      <div class="link-container">
        <DynamicChildrenComponent
          children={videoLink}
          passThroughProps={{
            customCssClass: 'HomepageClippingHero__revealContent',
          }}
        />
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/_media_queries' as mediaQueries;

  @layer custom-components {
    .overlay {
      .hero-text {
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: calc(92svw - clamp(16px, 5vw, 40px));
        text-align: center;
        max-width: 520px;
        @include mediaQueries.at('medium') {
          max-width: 1400px;
        }
        @include mediaQueries.until('medium') {
          @media (min-aspect-ratio: 8/5) {
            max-width: 1400px;
          }
        }
      }
      .link-container {
        position: absolute;
        bottom: 10%;
        left: 50%;
        transform: translateX(-50%);
        width: max-content;
        :global {
          .iconPlay {
            --icon-color: var(--textColor);
            &::before {
              border-left-color: black;
              transition: border-left-color var(--duration-quick) var(--easing-sharp);
            }
          }

          &:hover .iconPlay::before {
            border-left-color: white;
          }

          a,
          .iconText,
          .videoTextContainer {
            color: var(--textColor);
          }

          svg {
            fill: var(--textColor);
            transition: border-left-color var(--duration-quick) var(--easing-sharp);
          }
        }
      }
      .reveal-text {
        display: block;
      }

      &.absolute {
        position: absolute;
        width: 100%;
        height: 175svh;
        top: calc(-40px - var(--navHeight));
      }
      .sticky-text {
        position: sticky;
        top: 0;
        width: 100%;
        height: 100svh;
      }
      .invisible {
        visibility: hidden;
      }
      :global(.RichTextFieldComponent *) {
        color: var(--textColor);
        text-align: center;
        // font-family: 'Square Sans Display';
        font-size: 88px;
        font-size: clamp(44px, 4vw, 88px);
        font-style: normal;
        font-weight: 400;
        line-height: 96px;
        line-height: 1.2em;
      }
      @include mediaQueries.until('small') {
        :global(.RichTextFieldComponent *) {
          font-size: 36px;
        }
      }
      @include mediaQueries.until('medium') {
        @media (min-aspect-ratio: 8/5) {
          :global(.RichTextFieldComponent *) {
            font-size: 30px;
          }
        }
      }
      .button-line {
        margin-top: 56px;
        display: flex;
        justify-content: center;
        gap: 26px;
        flex-wrap: wrap;

        @include mediaQueries.at('small') {
          margin-right: 4px;
        }

        @include mediaQueries.until('medium') {
          gap: var(--space-20);
        }
        @include mediaQueries.until('medium') {
          @media (min-aspect-ratio: 8/5) {
            margin-top: var(--space-30);
          }
        }

        @include mediaQueries.until('small') {
          @media (min-aspect-ratio: 15/32) {
            margin-top: var(--space-30);
          }
        }
      }

      :global(.LinkComponent--button) {
        width: 180px;
      }

      :global(.LinkComponent--button-alt) {
        width: 180px;
      }

      &.before {
        z-index: 0;
        > .sticky-text {
          position: fixed;
          left: 50%;
          transform: translateX(-50%);
          width: calc(95% - clamp(16px, 5vw, 40px));
        }
        :global(.LinkComponent--button-alt) {
          background-color: white;
          border-color: white;
          color: var(--color-link);
        }
      }
      &.reveal {
        --textColor: black;
        &.absolute {
          width: calc(95% - clamp(16px, 5vw, 40px));
          left: 50%;
          transform: translateX(-50%);
        }
        & .hero-text {
          z-index: 0;
        }
      }
    }

    // adjustments for homepage 2.4 "short variant"
    :global(.short-variant) .overlay {
      &.absolute {
        inset: 0;
        height: auto;
      }

      &.before .sticky-text {
        position: relative;
        height: 100%;
        width: auto;
      }

      .link-container {
        display: none;
      }
    }
  }
</style>
